import { useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getMediaOptions, getProjectMediaOptions } from '@/api/media';
import { useUploadFloorLayouts as useUploadEstateFloorLayouts } from '@/features/estate-media/api/upload-floor-layout';
import { useUploadFloorLayouts as useUploadProjectFloorLayouts } from '@/features/project-media/api/upload-floor-layout';
import { FinishStep } from './upload-floor-plan/components/finish-step';
import { SelectEstateStep } from './upload-floor-plan/components/select-estate-step';
import { StepIndicator } from './upload-floor-plan/components/step-indicator';
import { UploadStep } from './upload-floor-plan/components/upload-step';
import { WizardFooter } from './upload-floor-plan/components/wizard-footer';
import { WizardHeader } from './upload-floor-plan/components/wizard-header';
import type {
  EstateProjectSearchItem,
  UploadFloorPlanFormProps,
  WizardStep,
} from './upload-floor-plan/types';
import { getFileKey, isAcceptedFloorPlanFile } from './upload-floor-plan/utils/floor-plan-file';

export const UploadFloorPlanForm: FC<UploadFloorPlanFormProps> = ({
  closeModal,
  label,
  onUploadingChange,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<WizardStep>(1);
  const [selectedItem, setSelectedItem] = useState<EstateProjectSearchItem>();
  const [files, setFiles] = useState<File[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const { mutateAsync: uploadEstateFloorLayouts, isPending: isUploadingEstateFloorLayouts } =
    useUploadEstateFloorLayouts();
  const { mutateAsync: uploadProjectFloorLayouts, isPending: isUploadingProjectFloorLayouts } =
    useUploadProjectFloorLayouts();

  const isUploading = isUploadingEstateFloorLayouts || isUploadingProjectFloorLayouts;
  const isBusy = isUploading || isCreating;

  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  const addFiles = (newFiles: File[]) => {
    const acceptedFiles = newFiles.filter(isAcceptedFloorPlanFile);
    const rejectedFileCount = newFiles.length - acceptedFiles.length;

    if (rejectedFileCount > 0) {
      toast.error(t('invalidFloorPlanFiles'));
    }

    const existingFileKeys = new Set(files.map(getFileKey));
    const uniqueNewFiles = acceptedFiles.filter((file) => {
      const fileKey = getFileKey(file);
      const isDuplicate = existingFileKeys.has(fileKey);

      existingFileKeys.add(fileKey);

      return !isDuplicate;
    });

    if (uniqueNewFiles.length < acceptedFiles.length) {
      toast.error(t('duplicateFilesIgnored'));
    }

    setFiles([...files, ...uniqueNewFiles]);
  };

  const removeFile = (fileKey: string) => {
    setFiles((currentFiles) => currentFiles.filter((file) => getFileKey(file) !== fileKey));
  };

  const selectItem = (item: EstateProjectSearchItem) => {
    const isDifferentItem =
      selectedItem && (selectedItem.id !== item.id || selectedItem.type !== item.type);

    if (isDifferentItem) {
      setFiles([]);
    }

    setSelectedItem(item);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleContinue = () => {
    if (selectedItem) setStep(2);
  };

  const handleUploadStepContinue = () => {
    if (selectedItem && files.length > 0) setStep(3);
  };

  const uploadFloorPlans = async () => {
    if (!selectedItem || files.length === 0) return;

    try {
      if (selectedItem.type === 'estate') {
        await uploadEstateFloorLayouts({ layouts: files, estateId: selectedItem.id });
        await queryClient.invalidateQueries({
          queryKey: getMediaOptions(selectedItem.id).queryKey,
        });
      } else {
        await uploadProjectFloorLayouts({ layouts: files, projectId: selectedItem.id });
        await queryClient.invalidateQueries({
          queryKey: getProjectMediaOptions(selectedItem.id).queryKey,
        });
      }

      toast.success(t('floor-plan-uploaded-success'));
      closeModal();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('somethingWentWrong'));
    }
  };

  return (
    <div className="relative flex max-h-[calc(100dvh-3rem)] w-full min-w-0 flex-col overflow-hidden">
      <WizardHeader label={label} closeModal={closeModal} disabled={isBusy} />
      <StepIndicator step={step} />

      {step === 1 ? (
        <SelectEstateStep
          selectedItem={selectedItem}
          onSelect={selectItem}
          onCreateAndContinue={(item) => {
            selectItem(item);
            setStep(2);
          }}
          onCreatingChange={setIsCreating}
        />
      ) : null}

      {step === 2 && selectedItem ? (
        <UploadStep
          files={files}
          isUploading={isUploading}
          selectedItem={selectedItem}
          onAddFiles={addFiles}
          onChangeSelection={handleBack}
          onRemoveFile={removeFile}
        />
      ) : null}

      {step === 3 && selectedItem ? (
        <FinishStep
          isUploading={isUploading}
          selectedItem={selectedItem}
          onUpload={uploadFloorPlans}
        />
      ) : null}

      <WizardFooter
        canContinue={Boolean(selectedItem) && !isCreating}
        canUpload={files.length > 0 && !isBusy}
        isBusy={isBusy}
        step={step}
        onBack={handleBack}
        onCancel={closeModal}
        onContinue={handleContinue}
        onUpload={handleUploadStepContinue}
      />
    </div>
  );
};
